import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Shield, Activity, ServerCrash, Users, Clock, AlertTriangle,
  CheckCircle2, XCircle, Download, RefreshCw, Loader2, Fingerprint, Radar,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid,
  Legend, BarChart, Bar,
} from "recharts";

// ---------- constants ------------------------------------------------------

const WINDOWS = [
  { key: "24h", label: "24 hours" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "90d", label: "90 days" },
];

const TABS = [
  { key: "overview", label: "Overview", icon: Shield },
  { key: "activity", label: "Authentication", icon: Activity },
  { key: "providers", label: "Providers", icon: ServerCrash },
  { key: "scim", label: "SCIM Governance", icon: Users },
  { key: "timeline", label: "Timeline", icon: Clock },
  { key: "risk", label: "Risk", icon: Radar },
];

const RISK_COLORS = {
  critical: { bg: "#FEE2E2", text: "#991B1B", dot: "#DC2626" },
  high: { bg: "#FEF3C7", text: "#92400E", dot: "#F59E0B" },
  medium: { bg: "#DBEAFE", text: "#1E40AF", dot: "#3B82F6" },
  low: { bg: "#DCFCE7", text: "#166534", dot: "#22C55E" },
};

// ---------- small building blocks -----------------------------------------

function Kpi({ label, value, sub, tone = "default", testid }) {
  const toneClass =
    tone === "critical" ? "border-[#FEE2E2] bg-[#FEF2F2]" :
    tone === "warn" ? "border-[#FEF3C7] bg-[#FFFBEB]" :
    tone === "good" ? "border-[#DCFCE7] bg-[#F0FDF4]" :
    "border-[#E5E5E0] bg-white";
  return (
    <div
      className={`rounded-2xl border p-5 ${toneClass}`}
      data-testid={testid}
    >
      <div className="text-[11px] uppercase tracking-widest text-[#7E937D] mb-2">{label}</div>
      <div className="text-3xl font-semibold text-[#1E3A8A]">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
}

function SeverityBadge({ severity }) {
  const c = RISK_COLORS[severity] || RISK_COLORS.low;
  return (
    <span
      style={{ background: c.bg, color: c.text }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] uppercase tracking-widest font-medium"
      data-testid={`severity-${severity}`}
    >
      <span className="w-2 h-2 rounded-full" style={{ background: c.dot }} />
      {severity}
    </span>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

function fmtPct(n) {
  return typeof n === "number" ? `${n.toFixed(1)}%` : "—";
}

// ---------- data hook ------------------------------------------------------

function useIdentityData(windowKey) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const [summary, trends, providers, timeline, risk, scim] = await Promise.all([
        api.get(`/identity-governance/summary?window=${windowKey}`),
        api.get(`/identity-governance/trends?window=${windowKey}`),
        api.get(`/identity-governance/providers`),
        api.get(`/identity-governance/timeline?limit=200`),
        api.get(`/identity-governance/risk?window=${windowKey}`),
        api.get(`/identity-governance/scim/dashboard?window=${windowKey}`),
      ]);
      setState({
        loading: false,
        error: null,
        data: {
          summary: summary.data,
          trends: trends.data,
          providers: providers.data.providers || [],
          timeline: timeline.data.events || [],
          risk: risk.data,
          scim: scim.data,
        },
      });
    } catch (err) {
      setState({ loading: false, error: formatApiError(err), data: null });
    }
  }, [windowKey]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

// ---------- tab bodies -----------------------------------------------------

function OverviewTab({ data }) {
  const s = data.summary;
  const risk = data.risk;
  const scoreTone = s.identity_risk_level === "critical" ? "critical" :
                    s.identity_risk_level === "high" ? "warn" :
                    s.identity_risk_level === "low" ? "good" : "default";
  return (
    <div className="space-y-6" data-testid="tab-overview">
      <div className="grid grid-cols-4 gap-4">
        <Kpi label="Identity Security Score" value={s.identity_security_score} sub={`Risk: ${s.identity_risk_level}`}
             tone={scoreTone} testid="kpi-score" />
        <Kpi label="Authentication Success" value={fmtPct(s.auth.success_rate_pct)}
             sub={`${s.auth.success} / ${s.auth.total}`} testid="kpi-auth-success" />
        <Kpi label="Failed Authentications" value={s.auth.failed} sub={fmtPct(s.auth.failed_rate_pct)}
             tone={s.auth.failed > 20 ? "warn" : "default"} testid="kpi-auth-failed" />
        <Kpi label="MFA Adoption" value={fmtPct(s.mfa.adoption_pct)}
             sub={`${s.mfa.enrolled} / ${s.mfa.total_users} users`}
             tone={s.mfa.adoption_pct < 60 ? "warn" : "good"} testid="kpi-mfa" />
      </div>
      <div className="grid grid-cols-4 gap-4">
        <Kpi label="Active Identity Providers" value={s.idps.count} sub={s.idps.active.join(", ") || "—"} testid="kpi-idps" />
        <Kpi label="Active SSO Tenants" value={s.tenants.configured}
             sub={`SAML ${s.tenants.saml_enabled} · OIDC ${s.tenants.oidc_enabled} · SCIM ${s.tenants.scim_enabled}`}
             testid="kpi-tenants" />
        <Kpi label="SCIM Health" value={fmtPct(s.scim.health_pct)}
             sub={`${s.scim.success} / ${s.scim.total_requests} ops`} testid="kpi-scim-health" />
        <Kpi label="Suspicious Events" value={s.suspicious_events}
             sub={`${risk.counts.critical} critical · ${risk.counts.high} high`}
             tone={risk.counts.critical ? "critical" : risk.counts.high ? "warn" : "good"}
             testid="kpi-suspicious" />
      </div>

      {/* Provisioning breakdown */}
      <div className="grid grid-cols-2 gap-4">
        <Kpi label="Provisioning Success" value={fmtPct(s.scim.provisioning.success_pct)}
             sub={`${s.scim.provisioning.success} of ${s.scim.provisioning.attempts} POSTs`} testid="kpi-provisioning" />
        <Kpi label="Deprovisioning Success" value={fmtPct(s.scim.deprovisioning.success_pct)}
             sub={`${s.scim.deprovisioning.success} of ${s.scim.deprovisioning.attempts} DELETEs`} testid="kpi-deprovisioning" />
      </div>

      {/* Trend chart */}
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-5" data-testid="chart-auth-outcome">
        <h3 className="text-sm font-semibold text-[#1E3A8A] mb-3">Authentication outcomes over time</h3>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={data.trends.auth_by_outcome}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E0" />
            <XAxis dataKey="t" tick={{ fontSize: 10 }} tickFormatter={fmtTime} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip labelFormatter={fmtTime} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="success" stroke="#16A34A" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="denied" stroke="#F59E0B" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="error" stroke="#DC2626" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ActivityTab({ data }) {
  const [filter, setFilter] = useState({ outcome: "", idp: "" });
  const filtered = useMemo(() => data.timeline
    .filter((e) => e.category === "authentication")
    .filter((e) => !filter.outcome || e.outcome === filter.outcome)
    .filter((e) => !filter.idp || e.target === filter.idp),
  [data.timeline, filter]);
  const idps = useMemo(() => Array.from(new Set(data.timeline.filter(e => e.category === "authentication").map(e => e.target).filter(Boolean))), [data.timeline]);

  return (
    <div className="space-y-4" data-testid="tab-activity">
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={filter.outcome}
          onChange={(e) => setFilter({ ...filter, outcome: e.target.value })}
          data-testid="activity-filter-outcome"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
        >
          <option value="">All outcomes</option>
          <option value="success">Success</option>
          <option value="denied">Denied</option>
          <option value="error">Error</option>
          <option value="redirect">Redirect</option>
        </select>
        <select
          value={filter.idp}
          onChange={(e) => setFilter({ ...filter, idp: e.target.value })}
          data-testid="activity-filter-idp"
          className="px-3 py-2 rounded-lg border border-[#E5E5E0] bg-white text-sm"
        >
          <option value="">All identity providers</option>
          {idps.map((i) => <option key={i} value={i}>{i}</option>)}
        </select>
        <span className="text-xs text-slate-500 ml-auto">{filtered.length} events</span>
      </div>

      <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden" data-testid="activity-table">
        <table className="w-full">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">When</th>
              <th className="text-left px-4 py-3">Actor</th>
              <th className="text-left px-4 py-3">IdP</th>
              <th className="text-left px-4 py-3">Outcome</th>
              <th className="text-left px-4 py-3">Detail</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E0]">
            {filtered.slice(0, 100).map((e, idx) => (
              <tr key={idx} data-testid="activity-row">
                <td className="px-4 py-2 text-xs text-slate-500">{fmtTime(e.at)}</td>
                <td className="px-4 py-2 text-sm">{e.actor || "—"}</td>
                <td className="px-4 py-2 text-sm font-mono">{e.target || "—"}</td>
                <td className="px-4 py-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    e.outcome === "success" ? "bg-[#DCFCE7] text-[#166534]" :
                    e.outcome === "denied" ? "bg-[#FEF3C7] text-[#92400E]" :
                    e.outcome === "error" ? "bg-[#FEE2E2] text-[#991B1B]" :
                    "bg-[#F1F5F9] text-[#475569]"
                  }`}>{e.outcome}</span>
                </td>
                <td className="px-4 py-2 text-xs text-slate-500">{e.detail || "—"}</td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center py-8 text-slate-500 text-sm">No events match the current filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProvidersTab({ data }) {
  return (
    <div className="space-y-4" data-testid="tab-providers">
      <div className="rounded-2xl border border-[#E5E5E0] bg-white overflow-hidden">
        <table className="w-full">
          <thead className="bg-[#F9F8F6] text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Provider</th>
              <th className="text-left px-4 py-3">Type</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Last event</th>
              <th className="text-left px-4 py-3">Last outcome</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E5E5E0]">
            {data.providers.map((p) => (
              <tr key={p.provider} data-testid={`provider-row-${p.provider.replace(/[:/]/g, "-")}`}>
                <td className="px-4 py-3 text-sm">{p.display_name}</td>
                <td className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500">{p.type}</td>
                <td className="px-4 py-3">
                  {p.configured && p.enabled ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#166534]">Enabled</span>
                  ) : p.configured ? (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E]">Configured</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#F1F5F9] text-[#475569]">Not configured</span>
                  )}
                  {p.stale && (
                    <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-[#FEE2E2] text-[#991B1B]">Stale</span>
                  )}
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{fmtTime(p.last_event_at)}</td>
                <td className="px-4 py-3 text-xs">{p.last_outcome || "—"}</td>
              </tr>
            ))}
            {data.providers.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-slate-500 text-sm">No identity providers configured.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ScimTab({ data, reload }) {
  const s = data.scim;
  const [busy, setBusy] = useState(null);

  const resolveOne = async (eventId) => {
    setBusy(eventId);
    try {
      await api.post(`/identity-governance/scim/failures/${eventId}/resolve`);
      toast.success("Failure marked resolved");
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };
  const clearAll = async () => {
    setBusy("all");
    try {
      const r = await api.post("/identity-governance/scim/failures/clear-resolved");
      toast.success(`Cleared ${r.data.resolved_count} failure(s)`);
      await reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  return (
    <div className="space-y-4" data-testid="tab-scim">
      <div className="grid grid-cols-4 gap-4">
        <Kpi label="Users provisioned" value={s.users_provisioned} testid="scim-users-provisioned" />
        <Kpi label="Users updated" value={s.users_updated} testid="scim-users-updated" />
        <Kpi label="Users deprovisioned" value={s.users_deprovisioned} testid="scim-users-deprov" />
        <Kpi label="Failed syncs" value={s.failed_synchronizations}
             tone={s.failed_synchronizations > 0 ? "warn" : "good"} testid="scim-failed" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <Kpi label="Avg latency" value={`${s.average_latency_ms} ms`} testid="scim-latency" />
        <Kpi label="Retry queue depth" value={s.retry_queue_depth} sub="Inbound SCIM is not retried locally" testid="scim-retry" />
        <Kpi label="Tenants active" value={Object.keys(s.last_sync_per_tenant || {}).length} testid="scim-tenant-count" />
      </div>
      {s.recent_failures.length > 0 && (
        <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-4"
              data-testid="scim-recent-failures">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold text-[#991B1B]">Recent failures</h4>
            <button onClick={clearAll} disabled={busy === "all"}
              data-testid="scim-clear-resolved-btn"
              className="text-xs inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white border border-[#FCA5A5] text-[#991B1B] hover:bg-[#FEF2F2] disabled:opacity-50">
              {busy === "all" ? "…" : "Clear all resolved"}
            </button>
          </div>
          <ul className="space-y-2 text-xs">
            {s.recent_failures.map((f) => (
              <li key={f.id}
                  className="rounded-lg bg-white border border-[#FEE2E2] p-3"
                  data-testid={`scim-failure-${f.id?.slice(0, 8)}`}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 font-mono">
                      <span className="font-semibold">{f.method}</span>
                      <span className="truncate max-w-[380px]">{f.path}</span>
                      <span className="px-1.5 py-0.5 rounded bg-[#FEE2E2] text-[#991B1B]">{f.status}</span>
                    </div>
                    {f.error_detail && (
                      <div className="text-slate-700 mt-1">{f.error_detail}</div>
                    )}
                    {f.request_payload && (
                      <details className="mt-1">
                        <summary className="cursor-pointer text-slate-500">
                          Show request payload
                        </summary>
                        <pre className="mt-1 p-2 bg-slate-50 border rounded text-[10px] overflow-auto whitespace-pre-wrap">
                          {typeof f.request_payload === "string"
                            ? f.request_payload
                            : JSON.stringify(f.request_payload, null, 2)}
                        </pre>
                      </details>
                    )}
                    <div className="text-slate-500 mt-1">
                      {fmtTime(f.at)} · tenant <span className="font-mono">{f.tenant_slug}</span>
                    </div>
                  </div>
                  <button onClick={() => resolveOne(f.id)}
                    disabled={busy === f.id}
                    data-testid={`scim-resolve-btn-${f.id?.slice(0, 8)}`}
                    className="text-[11px] whitespace-nowrap px-3 py-1 rounded-full border border-[#166534] text-[#166534] bg-white hover:bg-[#F0FDF4] disabled:opacity-50">
                    {busy === f.id ? "…" : "Resolve"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function TimelineTab({ data }) {
  return (
    <div className="space-y-4" data-testid="tab-timeline">
      <div className="rounded-2xl border border-[#E5E5E0] bg-white p-4">
        {data.timeline.length === 0 && (
          <div className="text-center py-10 text-slate-500 text-sm" data-testid="timeline-empty">
            No identity events recorded yet. Timeline populates once users start authenticating.
          </div>
        )}
        <ol className="relative border-l border-[#E5E5E0] ml-3">
          {data.timeline.slice(0, 100).map((e, idx) => (
            <li key={idx} className="mb-4 ml-4" data-testid="timeline-item">
              <div className="absolute w-2.5 h-2.5 rounded-full mt-1.5 -left-[5px] bg-[#1E3A8A]" />
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="font-medium">{e.category === "authentication" ? "Auth" : "SCIM"}</span>
                  <span className="text-slate-500"> · {e.actor || "—"}</span>
                  <span className="text-slate-500"> → {e.target || "—"}</span>
                </div>
                <span className={`text-[11px] px-2 py-0.5 rounded-full ${
                  e.outcome === "success" ? "bg-[#DCFCE7] text-[#166534]" :
                  e.outcome === "error" ? "bg-[#FEE2E2] text-[#991B1B]" :
                  "bg-[#F1F5F9] text-[#475569]"
                }`}>{e.outcome}</span>
              </div>
              <div className="text-xs text-slate-500 mt-0.5">{fmtTime(e.at)} — {e.detail || "no detail"}</div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

function RiskTab({ data }) {
  const findings = data.risk.findings;
  return (
    <div className="space-y-4" data-testid="tab-risk">
      <div className="grid grid-cols-4 gap-3">
        {["critical", "high", "medium", "low"].map((sev) => (
          <div key={sev}
               className="rounded-xl border border-[#E5E5E0] bg-white p-4 flex items-center justify-between"
               data-testid={`risk-count-${sev}`}>
            <SeverityBadge severity={sev} />
            <span className="text-2xl font-semibold">{data.risk.counts[sev]}</span>
          </div>
        ))}
      </div>
      <div className="space-y-2" data-testid="risk-findings">
        {findings.length === 0 && (
          <div className="rounded-2xl border border-[#DCFCE7] bg-[#F0FDF4] p-6 text-center text-sm text-[#166534]">
            <CheckCircle2 className="w-6 h-6 inline-block mr-2" />
            No identity risk findings in this window.
          </div>
        )}
        {findings.map((f, idx) => (
          <div key={idx} className="rounded-xl border border-[#E5E5E0] bg-white p-4"
               data-testid={`risk-finding-${f.severity}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <SeverityBadge severity={f.severity} />
                  <span className="font-medium">{f.title}</span>
                </div>
                <div className="text-sm text-slate-600">{f.description}</div>
                {f.evidence && Object.keys(f.evidence).length > 0 && (
                  <div className="mt-2 text-xs font-mono text-slate-500">
                    {Object.entries(f.evidence).map(([k, v]) => (
                      <span key={k} className="mr-3">{k}: {String(v)}</span>
                    ))}
                  </div>
                )}
              </div>
              <span className="text-[10px] uppercase tracking-widest text-slate-400">{f.category}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- main -----------------------------------------------------------

export default function IdentitySecurity() {
  const [windowKey, setWindowKey] = useState("7d");
  const [tab, setTab] = useState("overview");
  const { loading, error, data, reload } = useIdentityData(windowKey);

  const exportFile = (kind, fmt) => {
    const base = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem("hw_access_token") || "";
    const url = `${base}/api/identity-governance/export.${fmt}?window=${windowKey}&kind=${kind}${token ? `&token=${encodeURIComponent(token)}` : ""}`;
    window.open(url, "_blank");
  };

  return (
    <div className="max-w-7xl mx-auto p-8" data-testid="identity-security-page">
      {/* header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="hw-eyebrow text-[#F59E0B] mb-1 flex items-center gap-2">
            <Fingerprint className="w-3.5 h-3.5" />
            Enterprise Identity
          </div>
          <h1 className="font-display text-3xl font-semibold text-[#1E3A8A]">
            Identity Security &amp; Governance
          </h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Live authentication, provisioning and risk telemetry across every configured identity
            provider. Backed by <code>sso_login_events</code> and <code>scim_events</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={windowKey}
            onChange={(e) => setWindowKey(e.target.value)}
            data-testid="window-selector"
            className="px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm"
          >
            {WINDOWS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
          </select>
          <button
            onClick={reload}
            data-testid="identity-refresh-btn"
            className="p-2 rounded-full border border-[#E5E5E0] bg-white hover:bg-slate-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
          <div className="relative" data-testid="identity-export-menu">
            <details className="group">
              <summary className="list-none inline-flex items-center gap-2 px-3 py-2 rounded-full border border-[#E5E5E0] bg-white text-sm cursor-pointer">
                <Download className="w-4 h-4" /> Export
              </summary>
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl border border-[#E5E5E0] shadow-lg z-40 py-1">
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-400">CSV</div>
                <button data-testid="export-timeline-csv"
                        onClick={() => exportFile("timeline", "csv")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Timeline events</button>
                <button data-testid="export-risk-csv"
                        onClick={() => exportFile("risk", "csv")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Risk findings</button>
                <button data-testid="export-scim-csv"
                        onClick={() => exportFile("scim", "csv")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">SCIM events</button>
                <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-widest text-slate-400 border-t border-[#F1F5F9] mt-1">PDF (SOC 2 evidence)</div>
                <button data-testid="export-summary-pdf"
                        onClick={() => exportFile("summary", "pdf")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Executive summary</button>
                <button data-testid="export-timeline-pdf"
                        onClick={() => exportFile("timeline", "pdf")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Timeline report</button>
                <button data-testid="export-risk-pdf"
                        onClick={() => exportFile("risk", "pdf")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">Risk findings</button>
                <button data-testid="export-scim-pdf"
                        onClick={() => exportFile("scim", "pdf")}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50">SCIM events</button>
              </div>
            </details>
          </div>
        </div>
      </div>

      {/* tabs */}
      <div className="flex items-center gap-1 border-b border-[#E5E5E0] mb-6 overflow-x-auto">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`identity-tab-${t.key}`}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm border-b-2 -mb-px ${
                active
                  ? "border-[#1E3A8A] text-[#1E3A8A] font-medium"
                  : "border-transparent text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {loading && (
        <div className="text-center py-10 text-slate-500" data-testid="identity-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block mr-2" />
          Loading identity telemetry…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-2xl border border-[#FCA5A5] bg-[#FEF2F2] p-6 flex items-start gap-3"
             data-testid="identity-error">
          <XCircle className="w-6 h-6 text-[#991B1B] flex-shrink-0" />
          <div>
            <div className="font-semibold text-[#991B1B]">Failed to load data</div>
            <div className="text-sm text-[#991B1B] mt-1">{error}</div>
          </div>
        </div>
      )}

      {!loading && !error && data && (
        <>
          {tab === "overview" && <OverviewTab data={data} />}
          {tab === "activity" && <ActivityTab data={data} />}
          {tab === "providers" && <ProvidersTab data={data} />}
          {tab === "scim" && <ScimTab data={data} reload={reload} />}
          {tab === "timeline" && <TimelineTab data={data} />}
          {tab === "risk" && <RiskTab data={data} />}
        </>
      )}
    </div>
  );
}
